import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { InputNumber, Select } from '@bedrock/components';
import ProTableForm, {
  FormTableRef,
} from '@mpc/common/src/components/ProTableForm';
import { DURATION_UNIT_OPTIONS } from '../utils';

export interface SecondaryOnlineDetailItem {
  /** 序号（只读，由 index+1 推导） */
  phase_no?: number;
  /** 联动阶段描述，如 "主线剧情 / 支线任务" */
  linkage_phase?: string;
  /** 预计上线开始 */
  online_begin?: string;
  /** 预计上线结束 */
  online_end?: string;
  /** 预计下线开始 */
  offline_begin?: string;
  /** 预计下线结束 */
  offline_end?: string;
  /** 联动上线时长数值 */
  online_duration?: number;
  /** 联动上线时长单位：week | month */
  online_duration_unit?: 'WEEK' | 'MONTH';
}

interface SecondaryOnlineDetailTableProps {
  value?: SecondaryOnlineDetailItem[];
  onChange?: (value: SecondaryOnlineDetailItem[]) => void;
  /** 只读模式：联动上线时长列也纯文本展示 */
  view?: boolean;
}

/** 父表单 watchChildFormValidate 在提交时调用的校验能力 */
export interface SecondaryOnlineDetailTableRef {
  validateFields: () => Promise<unknown>;
}

/** 日期/日期时间字符串 → YYYY-MM；空值兜底 '--' */
function toYearMonth(d?: string): string {
  if (!d) return '--';
  return d.substring(0, 7);
}

/** "YYYY-MM～YYYY-MM" 形式的周期展示；begin/end 任一为空（无完整区间）→ 仅显示 '--'（对齐用户期望：没填就一个 --，不显示 '--～--'） */
function formatPeriod(begin?: string, end?: string): string {
  if (!begin || !end) return '--';
  return `${toYearMonth(begin)}～${toYearMonth(end)}`;
}

/** 联动上线时长组合值（与 formItemKeyMap 对象形态对应，镜像 SupplierEditTable SupplierPickerValue 范式） */
interface DurationValue {
  duration?: number;
  unit?: 'WEEK' | 'MONTH';
}

/** 自定义组合控件：InputNumber + 单位 Select（addonAfter），镜像 SFormItem demo NameCodeInput 受控范式 */
const DurationInput: React.FC<{
  value?: DurationValue;
  onChange?: (v: DurationValue) => void;
}> = ({ value = {}, onChange }) => {
  return (
    <InputNumber
      value={value.duration}
      onChange={(val) => onChange?.({ ...value, duration: val ?? undefined })}
      min={0}
      precision={0}
      placeholder="请输入"
      direction="vertical"
      addonAfter={
        <Select
          value={value.unit}
          onChange={(val) =>
            onChange?.({ ...value, unit: val as 'WEEK' | 'MONTH' })
          }
          options={DURATION_UNIT_OPTIONS}
          bordered={false}
          style={{ width: 95 }}
        />
      }
    />
  );
};

const SecondaryOnlineDetailTable = forwardRef<
  SecondaryOnlineDetailTableRef,
  SecondaryOnlineDetailTableProps
>(({ value, onChange, view = false }, ref) => {
  const tableRef = useRef<FormTableRef>(null);

  // 暴露校验能力：委托内部 ProTableForm.form.validateFields()（镜像 OnlineDetailTable 范式）
  useImperativeHandle(
    ref,
    () => ({
      validateFields: () => {
        if (!tableRef.current?.form?.validateFields) {
          return Promise.resolve();
        }
        return tableRef.current.form.validateFields().catch((error: any) => {
          const errMessage = error?.errorFields?.[0]?.errors?.[0];
          return Promise.reject(errMessage);
        });
      },
    }),
    []
  );

  const formColumns = useMemo(() => {
    return () => [
      // 联动阶段：只显示数字序号（对齐参考图「联动阶段」列显示 1/2/3；去掉独立「序号」列）
      {
        title: '联动阶段',
        dataIndex: 'linkage_phase',
        width: 70,
        align: 'center' as const,
        render: (_t: any, _r: SecondaryOnlineDetailItem, index: number) =>
          index + 1,
      },
      // 预计上线周期：只读展示（online_begin ~ online_end，非编辑列）
      {
        title: '预计上线周期',
        dataIndex: '$online_period' as any,
        width: 220,
        render: (_t: any, record: SecondaryOnlineDetailItem) =>
          formatPeriod(record.online_begin, record.online_end),
      },
      // 预计下线周期：只读展示（offline_begin ~ offline_end，非编辑列）
      {
        title: '预计下线周期',
        dataIndex: '$offline_period' as any,
        width: 220,
        render: (_t: any, record: SecondaryOnlineDetailItem) =>
          formatPeriod(record.offline_begin, record.offline_end),
      },
      {
        // 必填星由 required: !view 自动渲染（对齐参考图「* 联动上线时长」；勿手动加星，避免与 required 自带星重复）
        title: '联动上线时长',
        dataIndex: '$duration' as any,
        width: 220,
        // 联动上线时长必填：数值（online_duration）与单位（online_duration_unit）均须填写（镜像 OnlineDetailTable $offline_at rules 范式）
        required: !view,
        // 只读态纯文本展示；编辑态用自定义组合控件 + 对象形态 formItemKeyMap 分发到 online_duration / online_duration_unit
        ...(view
          ? {
              render: (_t: any, record: SecondaryOnlineDetailItem) => {
                const unitLabel =
                  DURATION_UNIT_OPTIONS.find(
                    (o) =>
                      o.value?.toLowerCase() ===
                      record.online_duration_unit?.toLowerCase()
                  )?.name || '';
                if (record.online_duration == null)
                  return <span className="text-color-3">--</span>;
                return (
                  <span>
                    {record.online_duration}
                    {unitLabel}
                  </span>
                );
              },
            }
          : {
              formItem: {
                render: () => <DurationInput />,
                formItemKeyMap: {
                  duration: 'online_duration',
                  unit: 'online_duration_unit',
                },
                rules: [
                  {
                    validator: (_rule, value: any) => {
                      if (value?.duration == null || value?.unit == null) {
                        return Promise.reject(new Error('请填写联动上线时长'));
                      }
                      return Promise.resolve();
                    },
                  },
                ],
              },
            }),
      },
    ];
  }, [view]);

  return (
    <ProTableForm<SecondaryOnlineDetailItem>
      ref={tableRef}
      value={value}
      onChange={onChange}
      sortable={false}
      size="small"
      scroll={{ x: 'max-content' }}
      formColumns={formColumns as any}
    />
  );
});

SecondaryOnlineDetailTable.displayName = 'SecondaryOnlineDetailTable';

export default SecondaryOnlineDetailTable;
